import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { theme } from 'antd';

import { SpinCentered } from '@/comp/index';
import Super from '@/comp/Super';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import IconInMaintenance from '@/assets/icon-in-maintenance.svg?react';
import { CustomThemeToken } from '@/theme';

import SpecificPowerTooltip from './Tooltip/SpecificPowerTooltip';
import BaselineSpecificPowerTooltip from './Tooltip/BaselineSpecificPowerTooltip';
import getYearlyhGraphInitOptions from '../YearlyhGraphInitOptions';

const { useToken } = theme;

const SpecificPowerTab = observer(({ yearToPresent }) => {
  const { tunit } = useMeasurement();
  const { t } = useTranslation();
  const { energySavingsGraphStore, userStore } = useStores();
  const { token } = useToken() as unknown as CustomThemeToken;
  const [options, setOptions] = useState(getYearlyhGraphInitOptions(t, token));

  const categories = () => {
    const categories = [];
    const dateStart = moment().month(0);
    const dateEnd = moment().month(12);

    categories.push('0');
    while (dateEnd.diff(dateStart, 'months') > 0) {
      categories.push(dateStart.format('MMM'));
      dateStart.add(1, 'month');
    }

    return categories;
  };

  useEffect(() => {
    const newOptions = {
      ...getYearlyhGraphInitOptions(t, token),
      xAxis: {
        lineWidth: 2,
        lineColor: token.colorBgContainer,
        tickColor: token.colorBgContainer,
        showEmpty: true,
        labels: {
          formatter: function () {
            if (yearToPresent === moment().year() && this.pos === moment().month() + 1) {
              return `${t(this.value)}<span style="fill: ${token.colorError};"> *</span>`;
            } else {
              return t(this.value);
            }
          },
          style: {
            fontSize: '7.7px',
            color: token.colorTextSecondary,
          },
        },
        categories: categories(),
        type: 'category',
        min: 1,
        max: 12,
      },
      yAxis: [
        // yAxis 1
        {
          gridLineColor: token.colorBorderSecondary,
          lineColor: token.colorBorderSecondary,
          gridLineDashStyle: 'longdash',
          lineWidth: 2,
          title: {
            text: ReactDOMServer.renderToString(
              <Trans i18nKey={tunit('kW/(m3/min)')}>
                <Super />
              </Trans>,
            ),
            align: 'high',
            rotation: 0,
            y: -15,
            x: 30,
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
            offset: -11,
          },
          labels: {
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
            x: -9,
          },
          min: 0,
        },
      ],
      tooltip: {
        shared: false,
        shadow: false,
        shape: 'square',
        useHTML: true,
        padding: 0,
        backgroundColor: undefined,
        borderWidth: 0,
        borderRadius: 0,
        formatter: function () {
          switch (this.series.name) {
            case t('Specific Power'):
              return ReactDOMServer.renderToString(
                <SpecificPowerTooltip
                  value={this.y}
                  actualEnergy={this.point.actualEnergy}
                  volume={this.point.volume}
                  name={this.series.name}
                  measurementSystem={userStore?.user?.measurementSystem}
                  token={token}
                />,
              );

            case t('Specific Power Baseline'):
              return ReactDOMServer.renderToString(
                <BaselineSpecificPowerTooltip
                  value={this.y}
                  name={this.series.name}
                  measurementSystem={userStore?.user?.measurementSystem}
                  token={token}
                />,
              );

            default:
          }
        },
      },
      plotOptions: {
        column: {
          grouping: false,
          shadow: false,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            useHTML: true,
            formatter: function () {
              return this.point.isInMaintenance ? ReactDOMServer.renderToString(<IconInMaintenance />) : null;
            },
          },
        },
      },
      series: [
        {
          name: t('Specific Power Baseline'),
          type: 'line',
          dashStyle: 'longDash',
          color: token.Ecoplant_Charts.colorLinechart_2,
          data: energySavingsGraphStore.energySavingsGraphs?.baselineSpecificPowerSeries,
          legendIndex: 2,
          yAxis: 0,
          marker: {
            enabled: false,
          },
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
          },
        },
        {
          name: t('Specific Power'),
          color: token.Ecoplant_Charts.colorSpecificPowerValue,
          data: energySavingsGraphStore.energySavingsGraphs?.specificPowerSeries,
          pointPadding: 0.3,
          legendIndex: 1,
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
          },
        },
        {
          name: t('Maintenance Event'),
          type: 'scatter',
          legendIndex: 3,
          marker: {
            symbol: `url(${IconInMaintenance})`,
          },
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
            mouseOut: function (e) {
              e.preventDefault();
            },
            mouseOver: function (e) {
              e.preventDefault();
            },
            checkboxClick: function (e) {
              e.preventDefault();
            },
          },
        },
      ],
    };
    setOptions(newOptions);
  }, [energySavingsGraphStore.energySavingsGraphs, token, t]);

  return (
    <Container>
      {energySavingsGraphStore.isLoading ? (
        <SpinCentered className={'spinner'} />
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  height: 100%;
  width: 100%;
`;

export default SpecificPowerTab;
